// Client-side mirrors of the backend accept/dismiss side effects
// (apply_ai_fields_to_security / DismissAIFieldView), used to patch the
// store in place instead of refetching the whole cap table. SCAL-8959.

import { UNDERLYING_SECURITY_ALIAS } from 'common/constants/cap-table/capTable';
import type { CitationReferenceData } from 'services/reference-generator/types';
import { aiMetadataKeyOf, referenceFieldNameSet, SECURITY_FIELD_BY_AI_METADATA_KEY } from './aiFieldMap';
import { sameAiValue } from './aiNumericValue';
import { matchAiOption, normalizeAiModelValue, normalizeAiSelectValue } from './resolveAiOptionValue';
import type { AiSelectOption } from './resolveAiOptionValue';

type AnySecurity = Record<string, any>;

const refFieldName = (ref: any): string | undefined => ref?.field_name ?? ref?.fieldName;

/**
 * Whether a citation reference was AI-created (SCAL-8969). `created_source`
 * (SCAL-9166) is the durable provenance signal and wins whenever the payload
 * carries it; older payloads (mixed-deploy window) fall back to generation_job
 * presence - a nullable FK (SET_NULL on job deletion), so it is only a stopgap,
 * never the shipping mechanism. Lives with the backend-mirror patches (it gates
 * acceptRefsForFields) and is re-exported by reviewAggregator for the review
 * surfaces.
 */
export const isAiCreatedCitation = (
  citation: Pick<CitationReferenceData, 'created_source' | 'generation_job'> | null | undefined
): boolean => (citation?.created_source != null ? citation.created_source === 'ai' : citation?.generation_job != null);

// Shared field-keyed ref rewrite: apply `patch` to every ref whose field name is in
// `fieldNames` - matched across every spelling the refs may carry (cell alias, model
// key, raw camelCase; see aiFieldMap) - KEEPING the rest of the list intact.
// Shape-agnostic (snake_case security refs and camelCase cell refs both resolve via
// refFieldName), so the in-grid cell paths and the security patches share ONE
// implementation per action.
const patchRefsForFields = (
  refs: any[] | undefined,
  fieldNames: string[],
  patch: (ref: any) => any
): any[] | undefined => {
  if (!Array.isArray(refs)) {
    return refs;
  }
  const names = referenceFieldNameSet(fieldNames);
  return refs.map(r => (names.has(refFieldName(r) as string) ? patch(r) : r));
};

// Soft-decline (SCAL-8976): settle the fields' refs to dismissed - status AND the
// is_accepted mirror, exactly what the backend's settle_citations_for_fields
// (STATUS_DISMISSED) persists - keeping the rows so a later full fetch reconciles
// to the same reversible shape.
export const declineRefsForFields = (refs: any[] | undefined, fieldNames: string[]): any[] | undefined =>
  patchRefsForFields(refs, fieldNames, r => ({ ...r, status: 'dismissed', is_accepted: false }));

// Accept-side mirror (SCAL-9336): accepting a field also settles its AI-CREATED
// citation refs to accepted, as accept_ai_fields_service -> settle_citations_for_fields
// does for the generation job's refs. Analyst-created citations stay untouched: the
// backend settles only job refs, and an analyst ref the reviewer explicitly rejected
// must not flip back to accepted.
export const acceptRefsForFields = (refs: any[] | undefined, fieldNames: string[]): any[] | undefined =>
  patchRefsForFields(refs, fieldNames, r =>
    isAiCreatedCitation(r) ? { ...r, status: 'accepted', is_accepted: true } : r
  );

// Reopen mirror (SCAL-9262): un-accepting/un-dismissing a field returns its
// AI-CREATED refs to pending, as the backend's settle_citations_for_fields
// (STATUS_PENDING) does for the job's refs. is_accepted mirrors the model's
// status sync (pending => false, the SCAL-8962 "AI awaiting review" mapping).
// Analyst-created refs stay untouched - the backend never settled them, so the
// next full fetch reconciles them unchanged either way.
export const reopenRefsForFields = (refs: any[] | undefined, fieldNames: string[]): any[] | undefined =>
  patchRefsForFields(refs, fieldNames, r =>
    isAiCreatedCitation(r) ? { ...r, status: 'pending', is_accepted: false } : r
  );

// SCAL-9152: the SINGLE source for the cap-table grid's "color by reference" GREEN rule.
// A cell is GREEN (.cell-with-references) iff it carries an Excel/cap-table MAPPING
// reference. PDF citation references do NOT turn a cell green - they only badge the value
// viewer (see conditions.tsx; MappingReference has no is_accepted, so presence alone is
// the rule). Accept/dismiss therefore never force green: the cell stays green only while a
// mapping reference survives, so green always reflects the cell's reference state, not the
// review action.
export const cellHasReferenceGreen = (cell: { mappingReferences?: unknown[] | null } | null | undefined): boolean =>
  (cell?.mappingReferences?.length ?? 0) > 0;

// Mirror of backend review_status_service.is_pending (SCAL-8976): a field is
// pending when present and not yet settled. status, when present, is
// authoritative ('pending' only); otherwise fall back to is_accepted (truthy =>
// settled). This keeps soft-dismissed fields (is_accepted=false, status=dismissed)
// out of the pending set.
export const isPendingMeta = (meta: any): boolean => {
  if (meta == null) return false;
  if (meta.status) return meta.status === 'pending';
  return !meta.is_accepted;
};

export const pendingFieldNames = (security: AnySecurity): string[] => {
  const metadata = security?.ai_finding?.field_metadata ?? {};
  return Object.entries(metadata)
    .filter(([, meta]: [string, any]) => isPendingMeta(meta))
    .map(([fieldName]) => fieldName);
};

const UNDERLYING_SECURITY_KEY = aiMetadataKeyOf(UNDERLYING_SECURITY_ALIAS);

// Sibling securities as select options in the underlying-security cell's
// domain: option id = the sibling's security_ref, label = its name.
const siblingOptions = (siblings: AnySecurity[] | undefined): AiSelectOption[] | undefined =>
  siblings?.filter(s => s?.security_ref).map(s => ({ id: s.security_ref, name: s.name }));

// Coercion contract: the AI metadata `value` is written onto the security field
// as-is with TWO exceptions that mirror the backend's cast_ai_field_value (the
// store holds DB-domain values): the Yes/No rows and Cash-or-PIK map through
// normalizeAiModelValue to the model boolean / 0-1 enum - a raw 'No' on the
// store would re-parse into the grid, render SELECT OPTION and break ==Y/==N
// formulas. Everything else is uncoerced; the parser normalizes display and the
// next full fetch reconciles formatting. Two fields additionally need
// TRANSLATION rather than coercion (aiFieldMap): their metadata lives under
// the MODEL key (underlying_security / security_type_id) while the grid reads the
// CELL-alias field (underlying_security_ref / security_type), and the
// underlying-security value arrives as a security NAME that must become the
// matching sibling's security_ref - the grid option id, matched via the shared
// resolver (exact, then case-insensitive trimmed) within `siblings`, the
// securities array being patched. An unresolved name writes NOTHING: the field
// still settles (the server already accepted it), but the raw name never lands
// on any field.
export const applyAcceptToSecurity = (
  security: AnySecurity,
  fieldNames: string[],
  siblings?: AnySecurity[]
): AnySecurity => {
  const aiFinding = security?.ai_finding;
  if (!aiFinding?.field_metadata) return security;
  const targets = fieldNames.map(aiMetadataKeyOf).filter(key => isPendingMeta(aiFinding.field_metadata[key]));
  if (!targets.length) return security;

  const fieldMetadata = { ...aiFinding.field_metadata };
  const next: AnySecurity = {
    ...security,
    ai_finding: { ...aiFinding, field_metadata: fieldMetadata },
    // The backend accept settles the fields' linked citations too (SCAL-9336);
    // mirror it so a rebuild-from-store cannot resurrect an accepted field's
    // citation badge as pending. Mapping refs are untouched (accept never was).
    citation_references: acceptRefsForFields(security.citation_references, targets),
  };
  targets.forEach(key => {
    const meta = fieldMetadata[key];
    fieldMetadata[key] = { ...meta, status: 'accepted', is_accepted: true };
    const value =
      key === UNDERLYING_SECURITY_KEY
        ? matchAiOption(siblingOptions(siblings), meta.value)?.id
        : (normalizeAiModelValue(key, meta.value) ?? meta.value);
    if (value !== undefined) next[SECURITY_FIELD_BY_AI_METADATA_KEY[key] ?? key] = value;
  });
  return next;
};

// SCAL-9337: what a declined suggestion leaves behind. The AI value must never
// survive a reject, so when the current value IS the AI value (purely
// AI-generated rows, or an accepted-then-declined field) the caller must write
// back the entry's previous_value baseline - null when none exists (or on
// legacy metadata without the key), so the cell goes EMPTY and save validation
// demands an explicit entry; a real prior 0/false is restored as-is. A merge
// suggestion never applied its value (current != AI value), so nothing is
// written - a manual edit made after the AI run survives. Select-backed values
// (options given) compare and revert in the cell's ID domain via the shared
// resolver; an unresolvable baseline name still blanks. "Holds the AI value"
// is decided by sameAiValue - the SAME normalized comparison the merge/echo
// surfaces use - because the live value is a formatted/typed variant of the AI
// value (number vs '6,308,352', JSON-double spelling vs the DB's 15dp binary
// expansion); a raw string compare read those as "user changed it" and left
// the declined AI value in the cell (the SCAL-9337 purely-AI-row regression).
// A value that differs NUMERICALLY (or textually, for non-numeric fields)
// still bails: a genuinely user-modified value is never reverted.
export const resolveDeclineRevert = (
  meta: { value?: unknown; previous_value?: unknown } | null | undefined,
  currentValue: unknown,
  options?: readonly AiSelectOption[] | null,
  alias?: string
): { revert: boolean; value?: unknown } => {
  const aiValue = meta?.value;
  if (aiValue == null || currentValue == null || currentValue === '') {
    return { revert: false };
  }
  // Yes/No + Cash-or-PIK rows (normalizeAiSelectValue): compare AND revert in
  // the option-INDEX domain, whatever spelling each side arrives in - the AI
  // label, the model boolean/enum, a legacy raw string, or the index itself.
  // Model-domain callers (dismiss/reopen below) convert the index back on write.
  const normalizedAi = normalizeAiSelectValue(alias, aiValue);
  if (normalizedAi !== null) {
    if (normalizeAiSelectValue(alias, currentValue) !== normalizedAi) {
      return { revert: false };
    }
    const prev = meta?.previous_value;
    // An unmappable baseline still blanks - the AI value must never survive.
    return prev == null || prev === ''
      ? { revert: true, value: null }
      : { revert: true, value: normalizeAiSelectValue(alias, prev) };
  }
  const resolvedAi = Array.isArray(options) ? matchAiOption(options, aiValue)?.id : aiValue;
  if (resolvedAi == null || !sameAiValue(resolvedAi, currentValue)) {
    return { revert: false };
  }
  const prev = meta?.previous_value;
  if (prev == null || prev === '') {
    return { revert: true, value: null };
  }
  return { revert: true, value: Array.isArray(options) ? (matchAiOption(options, prev)?.id ?? null) : prev };
};

// SOFT decline (SCAL-8976): mirror the backend's reversible dismiss. The field
// metadata entry is KEPT (status='dismissed', is_accepted=false) instead of being
// removed, and matching citation refs are flipped is_accepted=false rather than
// dropped, so a later full fetch reconciles to the same soft shape. SCAL-9337:
// a field holding the AI value is reverted per resolveDeclineRevert (the
// underlying-security NAME resolves against `siblings`, like the accept path);
// `name` stays untouched - a declined AI-proposed name rejects the whole
// column (dismissAiColumn), never blanks it.
export const applyDismissToSecurity = (
  security: AnySecurity,
  fieldNames: string[],
  siblings?: AnySecurity[]
): AnySecurity => {
  const aiFinding = security?.ai_finding;
  if (!aiFinding?.field_metadata) return security;
  const targets = fieldNames.map(aiMetadataKeyOf).filter(key => aiFinding.field_metadata[key] != null);
  if (!targets.length) return security;

  const fieldMetadata = { ...aiFinding.field_metadata };
  const next: AnySecurity = {
    ...security,
    ai_finding: { ...aiFinding, field_metadata: fieldMetadata },
    citation_references: declineRefsForFields(security.citation_references, targets),
    mapping_references: declineRefsForFields(security.mapping_references, targets),
  };
  targets.forEach(key => {
    const meta = fieldMetadata[key];
    fieldMetadata[key] = { ...meta, status: 'dismissed', is_accepted: false };
    if (key === 'name') return;
    const field = SECURITY_FIELD_BY_AI_METADATA_KEY[key] ?? key;
    const { revert, value } = resolveDeclineRevert(
      meta,
      next[field],
      key === UNDERLYING_SECURITY_KEY ? siblingOptions(siblings) : undefined,
      field
    );
    // Yes/No + Cash-or-PIK reverts come back in the option-index domain; the
    // store holds MODEL-typed values (boolean / the 0-1 enum), so convert.
    if (revert) next[field] = normalizeAiModelValue(field, value) ?? value;
  });
  return next;
};

// Reopen (SCAL-9262): mirror the backend's reopen_ai_fields on a store security.
// Settled entries flip back to status='pending' (is_accepted=null, the
// field_metadata mirror of STATUS_PENDING) and the fields' AI-created refs return
// to pending. Un-accepting also takes back the value the accept wrote: the SAME
// resolveDeclineRevert rule as dismiss - revert to the previous_value baseline
// only while the field still HOLDS the AI value, so a manual edit made after
// acceptance survives. A previously DISMISSED entry reopens status-only (decline
// already reverted the value, SCAL-9337), and `name` is excluded like dismiss.
export const applyReopenToSecurity = (
  security: AnySecurity,
  fieldNames: string[],
  siblings?: AnySecurity[]
): AnySecurity => {
  const aiFinding = security?.ai_finding;
  if (!aiFinding?.field_metadata) return security;
  const targets = fieldNames.map(aiMetadataKeyOf).filter(key => aiFinding.field_metadata[key] != null);
  if (!targets.length) return security;

  const fieldMetadata = { ...aiFinding.field_metadata };
  const next: AnySecurity = {
    ...security,
    ai_finding: { ...aiFinding, field_metadata: fieldMetadata },
    citation_references: reopenRefsForFields(security.citation_references, targets),
    mapping_references: reopenRefsForFields(security.mapping_references, targets),
  };
  targets.forEach(key => {
    const meta = fieldMetadata[key];
    const wasAccepted = meta.status ? meta.status === 'accepted' : !!meta.is_accepted;
    fieldMetadata[key] = { ...meta, status: 'pending', is_accepted: null };
    if (!wasAccepted || key === 'name') return;
    const field = SECURITY_FIELD_BY_AI_METADATA_KEY[key] ?? key;
    const { revert, value } = resolveDeclineRevert(
      meta,
      next[field],
      key === UNDERLYING_SECURITY_KEY ? siblingOptions(siblings) : undefined,
      field
    );
    // Same model-domain conversion as dismiss for Yes/No + Cash-or-PIK reverts.
    if (revert) next[field] = normalizeAiModelValue(field, value) ?? value;
  });
  return next;
};

// Reflect a bulk review response onto the securities store: apply EVERY settled
// field per security (accept or dismiss). Used by the cap-table drawer's bulk
// handler when the batch settled a security NAME, which triggers a full grid
// re-parse FROM the securities store - so the patch must cover all settled
// fields, not just the names: any field left out would be rebuilt as pending,
// visually un-settling the rest of the batch (the "bulk decline lost 73 of 80"
// regression). Both underlying helpers are idempotent on already-settled fields.
export const applySettledFieldsToSecurities = (
  securities: AnySecurity[],
  settledBySecurity: Map<number, string[]>,
  action: 'accept' | 'decline'
): AnySecurity[] =>
  securities.map(s => {
    const fields = s.id != null ? settledBySecurity.get(s.id) : undefined;
    if (!fields?.length) {
      return s;
    }
    // Both actions resolve underlying-security names against THIS array (siblings).
    return action === 'accept'
      ? applyAcceptToSecurity(s, fields, securities)
      : applyDismissToSecurity(s, fields, securities);
  });

// Accept the field VALUE while REJECTING its citation (SCAL-9162). Citations are
// created INDEPENDENTLY of the value finding and can be wrong, so an analyst may
// keep the AI value yet unlink its (bad) citation. This is the third review action
// alongside applyAcceptToSecurity (accept value + keep citation) and
// applyDismissToSecurity (reject value + reject citation): it composes the SAME two
// primitives instead of duplicating them - applyAcceptToSecurity applies the value
// and stamps the field accepted, then the shared declineRefsForFields soft-unlinks
// the field's citation refs (dismissed + is_accepted=false, reversible - T8; it runs
// AFTER accept, so the rejection wins over accept's own ref settle). The MAPPING refs
// are deliberately left intact, so the green color-by-reference rule follows mapping
// presence (cellHasReferenceGreen), never the rejected citation. Returns the input
// unchanged when accept applied nothing (no pending target).
export const acceptValueRejectCitation = (
  security: AnySecurity,
  fieldNames: string[],
  siblings?: AnySecurity[]
): AnySecurity => {
  const metadata = security?.ai_finding?.field_metadata;
  if (!metadata) {
    return security;
  }
  // Only the PENDING fields are actually accepted (mirrors applyAcceptToSecurity's
  // internal target filter), so decline ONLY those fields' citations - NOT a sibling
  // field passed in the same call that had no pending suggestion. Mirrors
  // applyDismissToSecurity, which passes the pre-filtered `targets` to declineRefsForFields.
  const targets = fieldNames.map(aiMetadataKeyOf).filter(key => isPendingMeta(metadata[key]));
  if (!targets.length) {
    return security;
  }
  const accepted = applyAcceptToSecurity(security, targets, siblings);
  return {
    ...accepted,
    citation_references: declineRefsForFields(accepted.citation_references, targets),
  };
};

export const removeCitationFromSecurities = (securities: AnySecurity[], citationId: number): AnySecurity[] =>
  securities.map(s => {
    const refs = s.citation_references;
    if (!Array.isArray(refs)) return s;
    const next = refs.filter((r: any) => r?.id !== citationId);
    return next.length === refs.length ? s : { ...s, citation_references: next };
  });
